import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  Input,
  FormGroup,
  Label,
  Button,
  ButtonGroup,
  FormText,
  Spinner,
  TabContent,
  TabPane,
  Tooltip,
} from "reactstrap";
import React, { useEffect, useState, useCallback, useRef } from "react";
import Select from "react-select";
import { useHistory, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faInfoCircle,
  faCogs,
  faAd,
  faArrowLeft,
  faSearchPlus,
  faSearchMinus,
  faExpandArrowsAlt,
  faDesktop,
  faMobileAlt,
  faCopy,
  faCheckCircle,
  faPaintBrush,
} from "@fortawesome/free-solid-svg-icons";

import footballWidgetService from "../../services/api/football-widget-service.js";
import AdditionalSettings from "./AdditionalSettings";
import AdSettings from "./AdSettings";
import TemplateSettings from "./TemplateSettings";

const EditFootballWidget = () => {
  const history = useHistory();
  const { id } = useParams();

  const selectStyles = {
    menu: (base) => ({
      ...base,
      position: "absolute",
      zIndex: 9999,
    }),
  };

  const matchStatusOptions = [
    { label: "All Matches", value: "all" },
    { label: "Live", value: "started" },
    { label: "Scheduled", value: "not_started" },
    { label: "Completed", value: "completed" },
  ];

  const [formDetails, setFormDetails] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [allRounds, setAllRounds] = useState([]);
  const [tournamentRounds, setTournamentRounds] = useState([]);
  const [matches, setMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [matchSelectionType, setMatchSelectionType] = useState("all");
  const [selectedMatchStatus, setSelectedMatchStatus] = useState(
    matchStatusOptions.find((opt) => opt.value === "all")
  );
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState({});
  const [previewHtml, setPreviewHtml] = useState(
    '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding: 20px;"> <h3>Widget Preview</h3> <p>Loading widget data...</p> </div>'
  );
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [noMatchesFound, setNoMatchesFound] = useState(false);
  const [noMatchesForFilter, setNoMatchesForFilter] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [previewScale, setPreviewScale] = useState(1);
  const [previewMode, setPreviewMode] = useState("desktop");
  const previewContainerRef = useRef(null);
  const isInitialLoad = useRef(true);
  const [isCopied, setIsCopied] = useState(false);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const fitPreviewToScreen = useCallback(() => {
    if (previewContainerRef.current) {
      setTimeout(() => {
        if (!previewContainerRef.current) return;
        const containerRect =
          previewContainerRef.current.getBoundingClientRect();

        if (previewMode === "desktop") {
          const virtualWidth = 1320;
          const virtualHeight = 760;
          if (containerRect.width > 0 && containerRect.height > 0) {
            const widthScale = containerRect.width / virtualWidth;
            const heightScale = containerRect.height / virtualHeight;
            const newScale = Math.min(widthScale, heightScale) * 0.95;
            setPreviewScale(newScale);
          }
        } else {
          const mobileViewport =
            previewContainerRef.current.querySelector(".mobile-viewport");
          if (mobileViewport) {
            const virtualWidth = 375 + 20;
            const virtualHeight = 812 + 20;
            if (containerRect.width > 0 && containerRect.height > 0) {
              const widthScale = containerRect.width / virtualWidth;
              const heightScale = containerRect.height / virtualHeight;
              const newZoom = Math.min(widthScale, heightScale) * 0.98;
              mobileViewport.style.zoom = newZoom;
            }
          }
        }
      }, 50);
    }
  }, [previewMode]);

  useEffect(() => {
    window.addEventListener("resize", fitPreviewToScreen);
    return () => {
      window.removeEventListener("resize", fitPreviewToScreen);
    };
  }, [fitPreviewToScreen]);

  useEffect(() => {
    fitPreviewToScreen();
  }, [previewMode, previewHtml, fitPreviewToScreen]);

  const handleModeChange = (mode) => {
    if (previewContainerRef.current) {
      const mobileViewport =
        previewContainerRef.current.querySelector(".mobile-viewport");
      if (mobileViewport) {
        mobileViewport.style.zoom = 1;
      }
    }
    setPreviewScale(1);
    setPreviewMode(mode);
  };

  const layouts = [
    { label: "Carousel", value: "carousel" },
    { label: "Listing", value: "listing" },
  ];

  const templates = [
    { label: "Tournament Matches / Single Card", value: "tournament" },
    { label: "Full Match Detail", value: "full-match-detail" },
    { label: "Points Table", value: "tournament-points-table" },
    { label: "Recent Tournaments List", value: "recent-tournaments-list" },
  ];

  const fetchMatchesForTournament = useCallback(async (tournamentSlug) => {
    try {
      const matchesRes = await footballWidgetService.getMatchesByTournament(
        tournamentSlug
      );
      const data = matchesRes.data?.data?.data || [];
      setAllMatches(data);
      setNoMatchesFound(false);
      return data;
    } catch (err) {
      setAllMatches([]);
      return [];
    }
  }, []);

  const fetchMatchesForRound = useCallback(async (roundSlug) => {
    try {
      const matchesRes = await footballWidgetService.getMatchesByRound(roundSlug);
      const data = matchesRes.data?.data?.data || [];
      setAllMatches(data);
      setNoMatchesFound(data.length === 0);
      return data;
    } catch (err) {
      setAllMatches([]);
      setNoMatchesFound(true);
      return [];
    }
  }, []);

  const fetchWidgetPreview = useCallback(
    async (payload) => {
      if (!payload) return;

      if (selectedRound && noMatchesFound) {
        setPreviewHtml(
          '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;"><h3>No Matches Found</h3><p>This round has no matches. Please select a different round.</p></div>'
        );
        return;
      }

      if (
        selectedRound &&
        noMatchesForFilter &&
        payload.template === "tournament" &&
        matchSelectionType === "all"
      ) {
        setPreviewHtml(
          `<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;">
           <h3>No ${selectedMatchStatus?.label || ""} Matches</h3>
           <p>There are no ${selectedMatchStatus?.label?.toLowerCase() || ""
          } matches in this round.</p>
         </div>`
        );
        return; 
      }

      const previewPayload = JSON.parse(JSON.stringify(payload));
      if (
        previewPayload.template === "tournament" &&
        matchSelectionType === "single" &&
        selectedMatch?.value
      ) {
        previewPayload.template = "single-match";
      }
      if (!previewPayload.template) {
        setPreviewHtml(
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a template to see preview</div>'
        );
        return;
      }
      const requiresTournamentSlug = [
        "tournament",
        "single-match",
        "full-match-detail",
        "tournament-points-table",
      ];
      if (
        requiresTournamentSlug.includes(previewPayload.template) &&
        !previewPayload.tournament_slug
      ) {
        setPreviewHtml(
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a tournament to see preview</div>'
        );
        return;
      }

      const requiresSingleMatchIntent =
        payload.template === "full-match-detail" ||
        (payload.template === "tournament" && matchSelectionType === "single");

      if (requiresSingleMatchIntent && previewPayload.match_slugs.length === 0) {
        if (noMatchesFound) {
          setPreviewHtml(
            '<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;"><h3>No Matches Found</h3><p>This round has no matches. Please select a different round.</p></div>'
          );
        } else if (noMatchesForFilter) {
          setPreviewHtml(
            `<div style="display:flex; flex-direction:column; align-items:center; justify-content:center; height:100%; font-family:sans-serif; color:#999; text-align:center; padding:20px;">
               <h3>No ${selectedMatchStatus?.label || ""} Matches</h3>
               <p>There are no ${selectedMatchStatus?.label?.toLowerCase() || ""
            } matches available to select.</p>
             </div>`
          );
        } else {
          setPreviewHtml(
            '<div style="display:flex;align-items:center;justify-content:center;height:100%;font-family:sans-serif;color:#666;">Select a match to see preview</div>'
          );
        }
        return; 
      }

      setIsFetchingPreview(true);
      try {
        const res = await footballWidgetService.getWidgetPreview(previewPayload);
        setPreviewHtml(
          res.status
            ? res.data
            : `<p style="color:red;font-family:sans-serif;padding:1rem;">Error: ${res.msg || "Could not load preview."
            }</p>`
        );
      } catch (error) {
        setPreviewHtml(
          '<p style="color:red;font-family:sans-serif;padding:1rem;">Could not fetch preview.</p>'
        );
      }
      setIsFetchingPreview(false);
    },
    [
      matchSelectionType,
      selectedMatch,
      noMatchesFound,
      selectedRound,
      noMatchesForFilter, 
      selectedMatchStatus,
    ]
  );

  useEffect(() => {
    const fetchAndPrefillData = async () => {
      isInitialLoad.current = true;
      let fetchedMatches = [];
      try {
        const [
          widgetDetailsRes,
          tournamentsRes,
          allRoundsRes,
          clientsRes,
          configRes,
        ] = await Promise.all([
          footballWidgetService.getFootballWidgetDetails(id),
          footballWidgetService.getFootballTournaments(),
          footballWidgetService.getFootballRounds(),
          footballWidgetService.getAllClients(),
          footballWidgetService.getConfigSchema(),
        ]);

        const allTournamentsData = tournamentsRes.status
          ? tournamentsRes.data.data
          : [];
        const allRoundsData = allRoundsRes.status ? allRoundsRes.data.data : [];
        const allClientsData =
          clientsRes.status && Array.isArray(clientsRes.data)
            ? clientsRes.data
            : [];
        if (configRes.status) setWidgetConfig(configRes.data);

        setTournaments(allTournamentsData);
        setAllRounds(allRoundsData);
        setPublisherOptions(
          allClientsData.map((p) => ({
            label: p.client_name,
            value: p.client_slug,
          }))
        );

        if (!widgetDetailsRes.status || !widgetDetailsRes.data) {
          toast.error("Could not load widget details.");
          return;
        }

        const widgetData = { ...widgetDetailsRes.data };
        const safeData = widgetData.data || { ads_data: { ad_codes: [] } };
        if (!safeData.template_data) {
          safeData.template_data = {};
        }

        let displayTemplate = widgetData.template;
        let initialMatchSelectionType = "all";

        if (widgetData.template === "single-match") {
          displayTemplate = "tournament";
          initialMatchSelectionType = "single";
        }
        setMatchSelectionType(initialMatchSelectionType);

        const initialFormDetails = {
          ...widgetData,
          template: displayTemplate,
          widget_data: safeData,
          domains: safeData.domains || [{ domain: "", isWildCard: false }],
          custom_css: safeData.custom_css || "",
        };
        setFormDetails(initialFormDetails);

        if (widgetData.client_slug) {
          const publisher = allClientsData.find(
            (p) => p.client_slug === widgetData.client_slug
          );
          if (publisher)
            setSelectedPublisher({
              label: publisher.client_name,
              value: publisher.client_slug,
            });
        }

        if (widgetData.tournament_slug) {
          const tournamentObj = allTournamentsData.find(
            (t) => t.t_slug === widgetData.tournament_slug
          );
          if (tournamentObj) {
            setSelectedTournament({
              label: tournamentObj.name,
              value: tournamentObj.t_slug,
            });

            const roundsForTournament = allRoundsData.filter((r) =>
              (tournamentObj.rounds || []).some((tr) => tr.key === r.key)
            );
            setTournamentRounds(roundsForTournament);

            if (widgetData.round_slug) {
              const roundObj = roundsForTournament.find(
                (r) => r.r_slug === widgetData.round_slug
              );
              if (roundObj) {
                setSelectedRound({
                  label: roundObj.name,
                  value: roundObj.r_slug,
                });
                fetchedMatches = await fetchMatchesForRound(
                  widgetData.round_slug
                );
              }
            } else {
              fetchedMatches = await fetchMatchesForTournament(
                widgetData.tournament_slug
              );
            }

            const singleMatchSlug = widgetData.match_slugs?.[0];
            if (singleMatchSlug && fetchedMatches.length > 0) {
              const prefilledMatch = fetchedMatches.find(
                (m) => m.match.m_slug === singleMatchSlug
              );
              if (prefilledMatch) {
                setSelectedMatch({
                  label: prefilledMatch.match.title,
                  value: prefilledMatch.match.m_slug,
                });
              }
            }
          }
        }
      } catch (err) {
        toast.error("Error fetching widget data!");
      } finally {
        isInitialLoad.current = false;
      }
    };
    fetchAndPrefillData();
  }, [id, fetchMatchesForTournament, fetchMatchesForRound]);

  useEffect(() => {
    if (isInitialLoad.current) return;
    if (selectedTournament?.value) {
      const tournamentSlug = selectedTournament.value;
      const tournament = tournaments.find((t) => t.t_slug === tournamentSlug);

      const roundsForTournament = allRounds.filter((r) =>
        (tournament?.rounds || []).some((tr) => tr.key === r.key)
      );
      setTournamentRounds(roundsForTournament);

      fetchMatchesForTournament(tournamentSlug).catch((err) =>
        toast.error("Failed to fetch matches for tournament.")
      );
      setSelectedRound(null);
      setSelectedMatch(null);
      setMatchSelectionType("all");
      setNoMatchesFound(false);
      setNoMatchesForFilter(false); 
      setFormDetails((prev) => ({
        ...prev,
        tournament_slug: tournamentSlug,
        round_slug: "",
        match_slugs: [],
      }));
    } else {
      setTournamentRounds([]);
      setAllMatches([]);
      setNoMatchesFound(false);
      setNoMatchesForFilter(false); 
      setFormDetails((prev) => ({
        ...prev,
        tournament_slug: "",
        round_slug: "",
        match_slugs: [],
      }));
    }
  }, [selectedTournament, tournaments, allRounds, fetchMatchesForTournament]);

  useEffect(() => {
    if (isInitialLoad.current) return;
    if (selectedRound?.value) {
      setAllMatches([]);
      setMatches([]);
      setNoMatchesFound(false);
      setNoMatchesForFilter(false);
      fetchMatchesForRound(selectedRound.value).catch((err) =>
        toast.error("Failed to fetch matches for round.")
      );
      setFormDetails((prev) => ({
        ...prev,
        round_slug: selectedRound.value,
        match_slugs: [],
      }));
    } else if (selectedTournament?.value) {
      fetchMatchesForTournament(selectedTournament.value).catch((err) =>
        toast.error("Failed to refetch tournament matches.")
      );
      setFormDetails((prev) => ({ ...prev, round_slug: "", match_slugs: [] }));
      setNoMatchesFound(false);
      setNoMatchesForFilter(false); 
    }
    setSelectedMatch(null);
  }, [selectedRound, selectedTournament, fetchMatchesForRound, fetchMatchesForTournament]);

  useEffect(() => {
    setSelectedMatch(null);
    const filtered = allMatches.filter(
      (match) =>
        !selectedMatchStatus ||
        selectedMatchStatus.value === "all" ||
        match.match.status === selectedMatchStatus.value
    );
    setMatches(filtered);

    if (
      filtered.length === 0 &&
      allMatches.length > 0 &&
      selectedMatchStatus &&
      selectedMatchStatus.value !== "all"
    ) {
      setNoMatchesForFilter(true);
    } else {
      setNoMatchesForFilter(false);
    }
  }, [selectedMatchStatus, allMatches]);

  useEffect(() => {
    if (isInitialLoad.current || !formDetails) return;

    let finalMatchSlugs = [];
    const isSingleMatchMode =
      formDetails.template === "full-match-detail" ||
      (formDetails.template === "tournament" && matchSelectionType === "single");

    if (isSingleMatchMode) {
      if (selectedMatch?.value) {
        finalMatchSlugs = [selectedMatch.value];
      }
    } else if (
      formDetails.template === "tournament" &&
      matchSelectionType === "all"
    ) {
      finalMatchSlugs = matches.map((m) => m.match.m_slug).filter(Boolean);
    }
    if (
      JSON.stringify(finalMatchSlugs) !== JSON.stringify(formDetails.match_slugs)
    ) {
      setFormDetails((prev) => ({ ...prev, match_slugs: finalMatchSlugs }));
    }
  }, [selectedMatch, matches, matchSelectionType, formDetails]);

  useEffect(() => {
    if (!formDetails) return;

    if (!isInitialLoad.current) {
      const handler = setTimeout(() => {
        fetchWidgetPreview(formDetails);
      }, 1000);
      return () => clearTimeout(handler);
    } else {
      fetchWidgetPreview(formDetails);
    }
  }, [formDetails, fetchWidgetPreview]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormDetails((prev) => ({ ...prev, [name]: value }));
  };

  const isValidDomain = (domain) =>
    /^(\*\.)?([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/.test(
      domain
    );

  const handleDomainChange = (index, value) => {
    const updatedDomains = [...(formDetails.domains || [])];
    updatedDomains[index] = { domain: value, isWildCard: value.startsWith("*.") };
    setFormDetails({ ...formDetails, domains: updatedDomains });
  };

  const handleAddDomain = () => {
    const lastDomain = (formDetails.domains || []).at(-1);
    if (
      (formDetails.domains || []).length > 0 &&
      (!lastDomain?.domain || !isValidDomain(lastDomain.domain))
    ) {
      return toast.error(
        "Please enter a valid domain before adding a new one."
      );
    }
    setFormDetails({
      ...formDetails,
      domains: [...(formDetails.domains || []), { domain: "", isWildCard: false }],
    });
  };

  const handleRemoveDomain = (index) => {
    const updatedDomains = (formDetails.domains || []).filter(
      (_, i) => i !== index
    );
    setFormDetails({ ...formDetails, domains: updatedDomains });
  };

  const handleLayoutChange = (selectedOption) => {
    setFormDetails((prev) => ({ ...prev, layout: selectedOption?.value || "" }));
  };

  const handleUpdate = async () => {
    if (!formDetails.widget_name.trim()) {
      toast.error("Widget Name is required.");
      return;
    }
    if (!formDetails.client_name.trim()) {
      toast.error("Publisher is required.");
      return;
    }
    if (!formDetails.template) {
      toast.error("Template is required.");
      return;
    }
    if (
      formDetails.template !== "recent-tournaments-list" &&
      !formDetails.tournament_slug
    ) {
      toast.error("Tournament is required for this template.");
      return;
    }
    if (formDetails.template === "tournament" && noMatchesFound) {
      toast.error("Cannot update widget. The selected round has no matches.");
      return;
    }

    const payload = { ...formDetails };
    if (
      payload.template === "tournament" &&
      matchSelectionType === "single" &&
      selectedMatch?.value
    ) {
      payload.template = "single-match";
    }

    const filteredDomains = (payload.domains || []).filter((d) => d.domain?.trim());
    payload.widget_data = {
      ...payload.widget_data,
      domains: filteredDomains,
      custom_css: payload.custom_css,
    };

    delete payload.domains;
    delete payload.custom_css;

    try {
      const result = await footballWidgetService.updateFootballWidget(id, payload);
      if (result.status) {
        toast.success("Widget Updated Successfully!");
        setTimeout(() => history.replace("/dashboard/football-widget"), 1500);
      } else {
        toast.error(result.msg || "Failed to update widget.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the widget.");
    }
  };

  const handleCopyEmbedCode = () => {
    if (formDetails.js_embed) {
      navigator.clipboard.writeText(formDetails.js_embed);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2500);
    }
  };

  if (!formDetails) {
    return (
      <div className="content d-flex justify-content-center align-items-center vh-100">
        <Spinner
          color="primary"
          style={{ width: "3rem", height: "3rem" }}
        >
          Loading...
        </Spinner>
      </div>
    );
  }

  const showDataSource =
    formDetails.template && formDetails.template !== "recent-tournaments-list";
  const isUpdateDisabled =
    isFetchingPreview || (formDetails.template === "tournament" && noMatchesFound);

  return (
    <div className="content">
      <style>{`
        .modern-tab { padding: 12px 20px; cursor: pointer; background: none; border: none; color: #6c757d; font-weight: 600; font-size: 0.9rem; transition: color 0.2s ease-in-out, border-bottom 0.2s ease-in-out; border-bottom: 3px solid transparent; }
        .modern-tab.active { color: #0d6efd; border-bottom: 3px solid #0d6efd; }
        .modern-tab:disabled { color: #adb5bd; cursor: not-allowed; } /* --- ADDED --- */
        .truncate-placeholder::placeholder {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
        .form-label { font-weight: 600; font-size: 0.85rem; color: #555; }
        .card { transition: box-shadow 200ms ease !important; }
        .preview-wrapper { width: 100%; background-color: #f8f9fa; border-radius: 0.5rem; padding: 1rem; position: sticky; top: 20px; }
        .preview-iframe-container { width: 100%; min-height: 500px; height: calc(100vh - 240px); border: 1px solid #dee2e6; border-radius: 0.25rem; position: relative; background-color: #e9ecef; display: flex; align-items: flex-start; justify-content: flex-start; overflow: auto; }
        .desktop-frame { transform-origin: top left; }
        .desktop-viewport, .mobile-viewport { flex-shrink: 0; background-color: #fff; will-change: transform, zoom; backface-visibility: hidden; }
        .desktop-frame { width: 1320px; height: 760px; padding: 20px; padding-top: 40px; border-radius: 10px; background: #f0f0f0; box-shadow: 0 8px 30px rgba(0,0,0,0.15); position: relative; }
        .desktop-header { position: absolute; top: 0; left: 0; width: 100%; height: 40px; background: #e0e0e0; border-top-left-radius: 10px; border-top-right-radius: 10px; display: flex; align-items: center; padding: 0 15px; }
        .dot { height: 12px; width: 12px; border-radius: 50%; margin-right: 8px; }
        .desktop-viewport { width: 1280px; height: 720px; box-shadow: 0 0 15px rgba(0,0,0,0.1); }
        .mobile-viewport { width: 375px; height: 812px; border-radius: 40px; box-shadow: 0 0 20px rgba(0,0,0,0.2); border: 10px solid #111; overflow: hidden; zoom: 1; margin: auto; }
        .preview-iframe { width: 100%; height: 100%; border: none; }
        .mobile-viewport .preview-iframe { border-radius: 30px; }
        .embed-code-textarea { background-color: #f1f3f5; border: 1px solid #dee2e6; color: #212529; font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 0.8em; resize: vertical; max-height: 120px; min-height: 70px; }
        .segmented-control { display: flex; position: relative; background-color: hsla(228, 50%, 62%, 1.00); border-radius: 8px; padding: 3px; width: fit-content; }
        .segmented-control__option { flex: 1; padding: 6px 16px; font-size: 0.8rem; font-weight: 600; color: #495057; background-color: transparent; border: none; cursor: pointer; transition: color 0.3s ease-in-out; z-index: 1; white-space: nowrap; }
        .segmented-control__glider { position: absolute; top: 3px; bottom: 3px; width: 49%; background-color: #ffffff; border-radius: 6px; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1); transition: transform 0.25s ease-in-out; z-index: 0; }
        .segmented-control__option:disabled { color: #adb5bd; cursor: not-allowed; }
      `}</style>
      <Container className="py-4" fluid>
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-0 fw-bold">📝 Edit Football Widget</h2>
                <p className="text-muted mb-0">
                  Modify your existing football widget and its settings.
                </p>
              </div>
              <div>
                <Button
                  color="secondary"
                  outline
                  className="me-2"
                  onClick={() => history.replace("/dashboard/football-widget")}
                >
                  <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back
                </Button>
                <Button
                  color="primary"
                  onClick={handleUpdate}
                  disabled={isUpdateDisabled}
                >
                  Update Widget
                </Button>
              </div>
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={5}>
            <Card className="shadow-sm border-0">
              <CardHeader className="bg-white border-0 py-0 px-3">
                <div className="d-flex border-bottom border-light w-100">
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "1",
                    })}
                    onClick={() => toggleTab("1")}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                    General
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "2",
                    })}
                    onClick={() => toggleTab("2")}
                  >
                    <FontAwesomeIcon icon={faCogs} className="me-2" />
                    Advanced
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "3",
                    })}
                    onClick={() => toggleTab("3")}
                    disabled={!formDetails.template}
                  >
                    <FontAwesomeIcon icon={faPaintBrush} className="me-2" />
                    Template
                  </button>
                  <button
                    className={classnames("modern-tab", {
                      active: activeTab === "4",
                    })}
                    onClick={() => toggleTab("4")}
                  >
                    <FontAwesomeIcon icon={faAd} className="me-2" />
                    Ads
                  </button>
                </div>
              </CardHeader>

              <CardBody className="p-4">
                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <Form>
                      <h5 className="fw-bold mb-3">General Information</h5>
                      <div className="d-flex flex-wrap gap-3">
                        <FormGroup style={{ flex: "1 1 100%" }}>
                          <Label className="form-label">
                            Widget Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            name="widget_name"
                            value={formDetails.widget_name}
                            onChange={handleInputChange}
                            autoComplete="off"
                          />
                        </FormGroup>
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label">
                            Publisher <span className="text-danger">*</span>
                          </Label>
                          <Select
                            styles={selectStyles}
                            isDisabled
                            options={publisherOptions}
                            value={selectedPublisher}
                          />
                        </FormGroup>
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label">
                            Template <span className="text-danger">*</span>
                          </Label>
                          <Select
                            styles={selectStyles}
                            options={templates}
                            value={templates.find(
                              (t) => t.value === formDetails.template
                            )}
                            onChange={(s) => {
                              const newTemplate = s ? s.value : "";
                              setFormDetails((p) => ({
                                ...p,
                                template: newTemplate,
                                layout:
                                  newTemplate === "tournament" ? "carousel" : "",
                                tournament_slug: "", 
                                round_slug: "",
                                match_slugs: [],
                                widget_data: {
                                  ...p.widget_data,
                                  template_data: {}, 
                                },
                              }));
                              setSelectedTournament(null);
                              setSelectedRound(null);
                              setAllMatches([]);
                              setSelectedMatch(null);
                              setMatchSelectionType("all"); 

                              setSelectedMatchStatus(
                                matchStatusOptions.find(
                                  (opt) => opt.value === "all"
                                )
                              );
                            }}
                          />
                        </FormGroup>
                        {formDetails.template === "tournament" && (
                          <FormGroup style={{ flex: "1 1 45%" }}>
                            <Label className="form-label">Layout</Label>
                            <Select
                              styles={selectStyles}
                              options={layouts}
                              value={layouts.find(
                                (l) => l.value === formDetails.layout
                              )}
                              onChange={handleLayoutChange}
                              placeholder="Select a layout"
                            />
                          </FormGroup>
                        )}
                        <FormGroup style={{ flex: "1 1 45%" }}>
                          <Label className="form-label ">Title</Label>
                          <Input
                            name="title"
                            className="truncate-placeholder"
                            value={formDetails.title}
                            onChange={handleInputChange}
                            placeholder="Widget Title (Optional)"
                          />
                        </FormGroup>
                      </div>

                      <hr className="mt-4 mb-3" />
                      <h5 className="fw-bold mb-3">Embed Code</h5>
                      <FormGroup className="d-flex align-items-center">
                        <Input
                          type="textarea"
                          readOnly
                          className="embed-code-textarea flex-grow-1"
                          value={formDetails.js_embed || "Not available"}
                          rows="3"
                        />
                        <Button
                          id="copyEmbedBtn"
                          size="sm"
                          color={isCopied ? "success" : "secondary"}
                          className="ms-2"
                          onClick={handleCopyEmbedCode}
                          disabled={!formDetails.js_embed}
                        >
                          <FontAwesomeIcon
                            icon={isCopied ? faCheckCircle : faCopy}
                          />
                        </Button>
                        <Tooltip
                          placement="top"
                          isOpen={tooltipOpen}
                          target="copyEmbedBtn"
                          toggle={toggleTooltip}
                        >
                          {isCopied ? "Copied!" : "Copy"}
                        </Tooltip>
                      </FormGroup>

                      {showDataSource && (
                        <>
                          <hr className="mt-4 mb-3" />
                          <h5 className="fw-bold mb-3">Football Info</h5>
                          <div className="d-flex flex-wrap gap-3">
                            <FormGroup style={{ flex: "1 1 100%" }}>
                              <Label className="form-label">
                                Tournament <span className="text-danger">*</span>
                              </Label>
                              <Select
                                styles={selectStyles}
                                options={tournaments.map((t) => ({
                                  value: t.t_slug,
                                  label: t.name,
                                }))}
                                onChange={setSelectedTournament}
                                value={selectedTournament}
                              />
                            </FormGroup>
                            {formDetails.template !==
                              "tournament-points-table" && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">Round</Label>
                                  <Select
                                    styles={selectStyles}
                                    options={tournamentRounds.map((r) => ({
                                      value: r.r_slug,
                                      label: r.name,
                                    }))}
                                    onChange={setSelectedRound}
                                    value={selectedRound}
                                    isDisabled={!selectedTournament}
                                    isClearable
                                  />
                                </FormGroup>
                              )}

                            {formDetails.template === "tournament" &&
                              selectedTournament && (
                                <FormGroup style={{ width: "100%" }}>
                                  <Label className="form-label d-block">
                                    Match Selection
                                  </Label>
                                  <div className="segmented-control">
                                    <div
                                      className="segmented-control__glider"
                                      style={{
                                        transform: `translateX(${matchSelectionType === "all"
                                            ? "0%"
                                            : "100%"
                                          })`,
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="segmented-control__option"
                                      onClick={() => {
                                        setMatchSelectionType("all");
                                        setSelectedMatch(null);
                                      }}
                                    >
                                      All Matches
                                    </button>
                                    <button
                                      type="button"
                                      className="segmented-control__option"
                                      onClick={() =>
                                        setMatchSelectionType("single")
                                      }
                                      disabled={noMatchesFound}
                                    >
                                      Single Match
                                    </button>
                                  </div>
                                </FormGroup>
                              )}

                            {(formDetails.template === "full-match-detail" ||
                              formDetails.template === "tournament") && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">
                                    Filter Match List by Status
                                  </Label>
                                  <Select
                                    styles={selectStyles}
                                    options={matchStatusOptions}
                                    onChange={setSelectedMatchStatus}
                                    value={selectedMatchStatus}
                                    isDisabled={!selectedTournament || noMatchesFound}
                                    isClearable
                                  />
                                </FormGroup>
                              )}

                            {(formDetails.template === "full-match-detail" ||
                              (formDetails.template === "tournament" &&
                                matchSelectionType === "single")) && (
                                <FormGroup style={{ flex: "1 1 45%" }}>
                                  <Label className="form-label">Match</Label>
                                  <Select
                                    styles={selectStyles}
                                    options={matches.map((m) => ({
                                      value: m.match.m_slug,
                                      label: m.match.title,
                                    }))}
                                    onChange={setSelectedMatch}
                                    value={selectedMatch}
                                    isDisabled={
                                      !selectedRound ||
                                      noMatchesFound ||
                                      noMatchesForFilter
                                    }
                                    placeholder={
                                      noMatchesFound
                                        ? "No matches available"
                                        : noMatchesForFilter
                                          ? `No ${selectedMatchStatus?.label || ""
                                          } matches`
                                          : "Select one match"
                                    }
                                  />
                                </FormGroup>
                              )}
                          </div>
                        </>
                      )}
                    </Form>
                  </TabPane>
                  <TabPane tabId="2">
                    <h5 className="fw-bold mb-3">Advanced Configuration</h5>
                    <div className="d-flex flex-wrap gap-3">
                      <div style={{ flex: "1 1 100%" }}>
                        {widgetConfig.additionalSettings ? (
                          <AdditionalSettings
                            additionalSettings={
                              widgetConfig.additionalSettings
                            }
                            formDetails={formDetails}
                            setFormDetails={setFormDetails}
                          />
                        ) : (
                          <p className="text-center text-muted">
                            No additional settings available.
                          </p>
                        )}
                      </div>
                      <div style={{ flex: "1 1 100%" }}>
                        <FormGroup>
                          <Label className="form-label">Custom CSS</Label>
                          <Input
                            type="textarea"
                            name="custom_css"
                            value={formDetails.custom_css || ""}
                            onChange={handleInputChange}
                            placeholder="Enter any custom CSS rules..."
                            rows={2}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label className="form-label">Allowed Domains</Label>
                          {(formDetails.domains || []).map((item, index) => (
                            <div key={index} className="d-flex mb-2">
                              <Input
                                type="text"
                                bsSize="sm"
                                placeholder="e.g., example.com or *.example.com"
                                value={item.domain}
                                onChange={(e) =>
                                  handleDomainChange(index, e.target.value)
                                }
                              />
                              <Button
                                color="danger"
                                outline
                                size="sm"
                                className="ms-2"
                                onClick={() => handleRemoveDomain(index)}
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </Button>
                              {index ===
                                (formDetails.domains || []).length - 1 && (
                                  <Button
                                    color="primary"
                                    outline
                                    size="sm"
                                    className="ms-2"
                                    onClick={handleAddDomain}
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </Button>
                                )}
                            </div>
                          ))}
                          <FormText>
                            Use `*.example.com` for wildcards. Leave empty to
                            allow all domains.
                          </FormText>
                        </FormGroup>
                      </div>
                    </div>
                  </TabPane>
                  <TabPane tabId="3">
                    <TemplateSettings
                      template={formDetails.template}
                      templateData={formDetails.widget_data.template_data}
                      setFormDetails={setFormDetails}
                      formDetails={formDetails}
                    />
                  </TabPane>
                  <TabPane tabId="4">
                    <h5 className="fw-bold mb-3">Advertisement Settings</h5>
                    {widgetConfig.adSettings ? (
                      <AdSettings
                        adConfig={widgetConfig.adSettings}
                        formDetails={formDetails}
                        setFormDetails={setFormDetails}
                      />
                    ) : (
                      <p className="text-center text-muted">
                        No ad settings available.
                      </p>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
          <Col md={7}>
            <div className="preview-wrapper">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold mb-0">Live Preview</h4>
                <div className="d-flex align-items-center">
                  <ButtonGroup size="sm" className="me-3">
                    <Button
                      outline
                      color="primary"
                      active={previewMode === "desktop"}
                      onClick={() => handleModeChange("desktop")}
                    >
                      <FontAwesomeIcon icon={faDesktop} />
                    </Button>
                    <Button
                      outline
                      color="primary"
                      active={previewMode === "mobile"}
                      onClick={() => handleModeChange("mobile")}
                    >
                      <FontAwesomeIcon icon={faMobileAlt} />
                    </Button>
                  </ButtonGroup>
                  {previewMode === "desktop" && (
                    <>
                      <ButtonGroup size="sm">
                        <Button
                          outline
                          color="secondary"
                          onClick={() =>
                            setPreviewScale((s) => Math.max(0.1, s - 0.1))
                          }
                        >
                          <FontAwesomeIcon icon={faSearchMinus} />
                        </Button>
                        <Button
                          outline
                          color="secondary"
                          onClick={fitPreviewToScreen}
                        >
                          <FontAwesomeIcon icon={faExpandArrowsAlt} />
                        </Button>
                        <Button
                          outline
                          color="secondary"
                          onClick={() => setPreviewScale((s) => s + 0.1)}
                        >
                          <FontAwesomeIcon icon={faSearchPlus} />
                        </Button>
                      </ButtonGroup>
                      <small
                        className="text-muted ms-2 fw-bold"
                        style={{ minWidth: "40px", textAlign: "right" }}
                      >
                        {Math.round(previewScale * 100)}%
                      </small>
                    </>
                  )}
                </div>
              </div>
              <div
                className="preview-iframe-container"
                ref={previewContainerRef}
              >
                {isFetchingPreview && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255,255,255,0.7)",
                      zIndex: 10,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spinner color="primary">Loading...</Spinner>
                  </div>
                )}
                {previewMode === "desktop" ? (
                  <div
                    style={{
                      width: 1320 * previewScale,
                      height: 760 * previewScale,
                      flexShrink: 0,
                      margin: "auto",
                    }}
                  >
                    <div
                      className="desktop-frame"
                      style={{ transform: `scale(${previewScale})` }}
                    >
                      <div className="desktop-header">
                        <div
                          className="dot"
                          style={{ background: "#ff5f56" }}
                        ></div>
                        <div
                          className="dot"
                          style={{ background: "#ffbd2e" }}
                        ></div>
                        <div
                          className="dot"
                          style={{ background: "#27c93f" }}
                        ></div>
                      </div>
                      <div className="desktop-viewport">
                        <iframe
                          srcDoc={previewHtml}
                          title="Widget Preview"
                          className="preview-iframe"
                          id="preview-iframe"
                          sandbox="allow-scripts"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mobile-viewport">
                    <iframe
                      srcDoc={previewHtml}
                      title="Widget Preview"
                      className="preview-iframe"
                      id="preview-iframe"
                      sandbox="allow-scripts"
                    />
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ToastContainer position="bottom-right" />
    </div>
  );
};

export default EditFootballWidget;